import { useEffect, useMemo, useState } from 'react';

import { RoomInfo } from '../types/app-types';
import { readGetRoomsInfo } from '../viem-functions/find-rooms';

import { Game } from 'modules/lobby-page/pages/lobby';

const toGameId = (game) => Number(game[0]);
const toRoomId = (room: RoomInfo) => Number((room as RoomInfo)?.data?.roomId);

const groupIntoRanges = (ids: number[], maxSpan = 50): Array<[number, number]> => {
  if (ids.length === 0) return [];
  const sorted = [...new Set(ids)].sort((a, b) => a - b);

  const ranges: Array<[number, number]> = [];
  let start = sorted[0];
  let end = sorted[0];

  for (let i = 1; i < sorted.length; i++) {
    const cur = sorted[i];
    // Extend current range if the span stays within maxSpan
    if (cur - start <= maxSpan) {
      end = cur;
    } else {
      ranges.push([start, end]);
      start = end = cur;
    }
  }
  ranges.push([start, end]);
  return ranges;
};

export const useGamesInfo = (activeGames: Game[] | undefined, publicClient: any) => {
  const [gamesInfo, setGamesInfo] = useState<RoomInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const ids = useMemo(() => {
    if (!activeGames?.length) return [];
    return Array.from(new Set(activeGames.map(toGameId)));
  }, [activeGames]);

  const ranges = useMemo(() => groupIntoRanges(ids, 50), [ids]);

  useEffect(() => {
    let cancelled = false;

    const fetchInfo = async () => {
      if (ids.length === 0) {
        setGamesInfo([]);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const results = await Promise.all(
          ranges.map(([minId, maxId]) => readGetRoomsInfo(publicClient, minId, maxId)),
        );
        const flat: any[] = ([] as any[]).concat(...results);

        // Map by room id (in case contract returns duplicates across ranges)
        const byId = new Map<number, RoomInfo>();
        for (const r of flat) {
          const id = toRoomId(r);
          if (Number.isFinite(id)) byId.set(id, r);
        }

        // Only keep entries that match the requested ids, preserve ids order
        const finalList: RoomInfo[] = ids.map((id) => byId.get(id)).filter(Boolean) as RoomInfo[];
        if (!cancelled) setGamesInfo(finalList);
      } catch (e) {
        if (!cancelled) setError(e as Error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchInfo();
    return () => {
      cancelled = true;
    };
  }, [ids, ranges, publicClient]);

  return { gamesInfo, loading, error };
};
